// src/pages/ShopScreen.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useROStore } from "../context/ROContext";
import { RO_LIFECYCLE, RO_STATUS } from "../constants/roStatus";

/* ===============================
   Helpers
================================ */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeUpper(v) {
  return safeString(v).trim().toUpperCase();
}

function firstArray(...values) {
  return values.find((v) => Array.isArray(v)) || [];
}

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTime(input) {
  const d = safeDate(input);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatTimestamp(input) {
  const d = safeDate(input) || new Date();
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function getPromiseBadge(promiseTimeISO, now) {
  const d = safeDate(promiseTimeISO);
  if (!d) return { label: "No promise", type: "none" };

  const diffMs = d.getTime() - now.getTime();
  const diffMins = diffMs / 60000;

  if (diffMins < 0) return { label: "Overdue", type: "overdue" };
  if (diffMins <= 60) return { label: "Due soon", type: "soon" };
  return { label: "On schedule", type: "ok" };
}

function isCompletedLike(ro) {
  const lifecycle = safeUpper(ro?.lifecycle || ro?.lifeCycle);
  const status = safeUpper(ro?.status);

  return (
    lifecycle === safeUpper(RO_LIFECYCLE?.COMPLETED || "COMPLETED") ||
    status === safeUpper(RO_STATUS?.COMPLETED || "COMPLETED") ||
    status === "REPAIRS_COMPLETE" ||
    status === "REPAIR_COMPLETE"
  );
}

function isVehicleGoneLike(ro) {
  const lifecycle = safeUpper(ro?.lifecycle || ro?.lifeCycle);
  const status = safeUpper(ro?.status);
  const pickupStatus = safeUpper(
    ro?.customerPickupStatus || ro?.customer_pickup_status
  );
  const advisorAction = safeUpper(
    ro?.advisorActionStatus || ro?.advisor_action_status
  );

  return (
    status === "VEHICLE_GONE" ||
    lifecycle === "VEHICLE_GONE" ||
    pickupStatus === "VEHICLE_GONE" ||
    advisorAction === "VEHICLE_GONE" ||
    !!ro?.vehicleGone ||
    !!ro?.vehicle_gone ||
    !!ro?.pickedUp ||
    !!ro?.picked_up
  );
}

function isArchivedLike(ro) {
  const lifecycle = safeUpper(ro?.lifecycle || ro?.lifeCycle);
  const status = safeUpper(ro?.status);

  return status === "ARCHIVED" || lifecycle === "ARCHIVED";
}

function isVisibleShopRO(ro) {
  if (!ro) return false;
  if (isArchivedLike(ro)) return false;
  if (isVehicleGoneLike(ro)) return false;
  if (isCompletedLike(ro)) return false;

  return true;
}

function getRoNumber(ro) {
  return (
    safeString(
      ro?.roNumber ||
        ro?.ro_number ||
        ro?.roId ||
        ro?.ro_id ||
        ro?.id ||
        ""
    ).trim() || "—"
  );
}

function getCustomerName(ro) {
  return (
    safeString(ro?.customerName || ro?.customer_name || ro?.customer || "").trim() ||
    "Customer"
  );
}

function getVehicle(ro) {
  return (
    safeString(
      ro?.vehicle ||
        ro?.vehicleDescription ||
        ro?.vehicle_description ||
        ""
    ).trim() || "Vehicle —"
  );
}

function getAssignedTechName(ro) {
  return safeString(
    ro?.assignedTechName ||
      ro?.assigned_tech_name ||
      ro?.technicianName ||
      ro?.technician_name ||
      ""
  ).trim();
}

function getPromiseTime(ro) {
  return (
    ro?.promiseTimeISO ||
    ro?.promise_time_iso ||
    ro?.promiseTime ||
    ro?.promise_time ||
    null
  );
}

function getDisplayStatus(ro) {
  const lifecycle = safeUpper(ro?.lifecycle || ro?.lifeCycle);
  const status = safeUpper(ro?.status);
  const partsStatus = safeUpper(ro?.partsStatus || ro?.parts_status);
  const partsAction = safeUpper(
    ro?.partsActionStatus || ro?.parts_action_status
  );
  const requestType = safeUpper(ro?.partsRequestType || ro?.parts_request_type);
  const hasTech = !!getAssignedTechName(ro) || !!safeString(ro?.assignedTechId).trim();

  if (lifecycle === "IN_PROGRESS" || status === "IN_PROGRESS") {
    return "IN PROGRESS";
  }

  if (
    requestType === "NEED_PRICE" &&
    (partsAction === "REVIEW" ||
      partsAction === "OPEN" ||
      partsAction === "REOPEN" ||
      !partsAction)
  ) {
    return "QUOTE NEEDED";
  }

  if (
    requestType === "NEED_PARTS" &&
    (partsAction === "REVIEW" ||
      partsAction === "OPEN" ||
      partsAction === "REOPEN" ||
      !partsAction)
  ) {
    return "PARTS NEEDED";
  }

  if (partsStatus === "ORDERED" || partsAction === "ORDERED") {
    return "PARTS ORDERED";
  }

  if (partsStatus === "ARRIVED" || partsAction === "ARRIVED") {
    return "PARTS ARRIVED";
  }

  if (
    partsStatus === "PULLED" ||
    partsAction === "PRE_PULLED" ||
    partsAction === "PREPULLED"
  ) {
    return "PARTS PRE-PULLED";
  }

  if (status === "ASSIGNED" || hasTech) return "ASSIGNED";

  if (
    status === "OPEN" ||
    status === "AVAILABLE" ||
    lifecycle === "OPEN" ||
    lifecycle === "WAITING_FOR_ASSIGNMENT"
  ) {
    return "AVAILABLE";
  }

  if (status) return status.replaceAll("_", " ");
  if (lifecycle) return lifecycle.replaceAll("_", " ");

  return "AVAILABLE";
}

function supportsFullscreen() {
  return !!(
    typeof document !== "undefined" &&
    document.documentElement &&
    (document.documentElement.requestFullscreen ||
      document.documentElement.webkitRequestFullscreen)
  );
}

const PAGE_ROTATION_MS = 12000;

function getViewportSnapshot() {
  if (typeof window === "undefined") {
    return { width: 1440, height: 900 };
  }

  return {
    width: window.innerWidth || 1440,
    height: window.innerHeight || 900,
  };
}

function getShopPageSize(viewport, isFullscreen) {
  const width = Number(viewport?.width) || 1440;
  const height = Number(viewport?.height) || 900;

  if (isFullscreen) {
    if (height < 680) return width >= 1180 ? 8 : 6;
    if (width >= 1500 && height >= 800) return 12;
    if (width >= 1180) return 9;
    return 6;
  }

  if (width >= 1350) return 12;
  if (width >= 1000) return 9;
  return 6;
}

/* ===============================
   Styles
================================ */
const shellBase = {
  minHeight: "100vh",
  background: "#0f172a",
  color: "#e5e7eb",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  gap: 14,
};

const titleStyle = {
  margin: 0,
  fontSize: 24,
  lineHeight: 1.02,
  fontWeight: 900,
  letterSpacing: "0.035em",
  textTransform: "uppercase",
  color: "#f8fafc",
};

const subtitleStyle = {
  marginTop: 5,
  fontSize: 13,
  color: "#9ca3af",
  lineHeight: 1.35,
};

const metricLabel = {
  color: "#94a3b8",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.09em",
  lineHeight: 1.1,
};

const tvButtonStyle = {
  border: "1px solid rgba(148,163,184,0.42)",
  background: "rgba(15,23,42,0.72)",
  color: "#e5e7eb",
  borderRadius: 11,
  minWidth: 40,
  height: 40,
  padding: "0 12px",
  fontSize: 14,
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};

function summaryTileStyle(tone = "default", isFullscreen = false) {
  let border = "rgba(148,163,184,0.34)";
  let background = "rgba(15,23,42,0.72)";

  if (tone === "danger") {
    border = "rgba(248,113,113,0.38)";
    background = "rgba(127,29,29,0.30)";
  }

  if (tone === "warn") {
    border = "rgba(251,191,36,0.38)";
    background = "rgba(120,53,15,0.26)";
  }

  return {
    border: `1px solid ${border}`,
    background,
    borderRadius: 13,
    padding: isFullscreen ? "10px 13px" : "9px 12px",
    minWidth: isFullscreen ? 108 : 96,
    boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
  };
}

function statusPillStyle() {
  return {
    fontSize: 11,
    borderRadius: 999,
    padding: "2px 8px",
    fontWeight: 900,
    lineHeight: 1.1,
    background: "rgba(30,41,59,0.92)",
    color: "#cbd5f5",
    whiteSpace: "nowrap",
  };
}

function promisePillStyle(type) {
  const base = {
    fontSize: 11,
    borderRadius: 999,
    padding: "3px 9px",
    fontWeight: 900,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  };

  if (type === "overdue") {
    return { ...base, background: "#ef4444", color: "#fef2f2" };
  }

  if (type === "soon") {
    return { ...base, background: "#f59e0b", color: "#fffbeb" };
  }

  if (type === "ok") {
    return { ...base, background: "#22c55e", color: "#ecfdf3" };
  }

  return { ...base, background: "#64748b", color: "#f8fafc" };
}

function shopCardStyle(type, isFullscreen = false) {
  let borderColor = "rgba(148,163,184,0.38)";
  let shadow = "0 8px 24px rgba(0,0,0,0.26)";
  let background = "#020617";

  if (type === "overdue") {
    borderColor = "rgba(254,202,202,0.94)";
    shadow = "0 0 0 1px rgba(248,113,113,0.42)";
  }

  if (type === "soon") {
    borderColor = "rgba(254,215,170,0.94)";
    shadow = "0 0 0 1px rgba(251,191,36,0.35)";
  }

  return {
    background,
    borderRadius: 16,
    padding: isFullscreen ? "15px 17px" : "14px 16px",
    minHeight: isFullscreen ? 126 : 124,
    boxShadow: shadow,
    border: `1px solid ${borderColor}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: 0,
  };
}

/* ===============================
   Component
================================ */
export default function ShopScreen() {
  const store = useROStore();
  const [now, setNow] = useState(() => new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenSupported, setFullscreenSupported] = useState(() =>
    supportsFullscreen()
  );
  const [viewport, setViewport] = useState(() => getViewportSnapshot());

  const shopSourceROs = useMemo(() => {
    /*
      Source-of-truth priority:
      Use the same WIP-visible arrays first so Shop Screen does not show
      old ROs that WIP Board already hides. Fall back to raw lists only if
      the context has not exposed a WIP-specific array.
    */
    return firstArray(
      store?.visibleBoardROs,
      store?.visibleWipROs,
      store?.visibleWIPROs,
      store?.wipROs,
      store?.openROs,
      store?.managerDashboardROs,
      store?.ros,
      store?.repairOrders
    );
  }, [
    store?.visibleBoardROs,
    store?.visibleWipROs,
    store?.visibleWIPROs,
    store?.wipROs,
    store?.openROs,
    store?.managerDashboardROs,
    store?.ros,
    store?.repairOrders,
  ]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setFullscreenSupported(supportsFullscreen());

    function updateFullscreenState() {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement)
      );
    }

    document.addEventListener("fullscreenchange", updateFullscreenState);
    document.addEventListener("webkitfullscreenchange", updateFullscreenState);

    updateFullscreenState();

    return () => {
      document.removeEventListener("fullscreenchange", updateFullscreenState);
      document.removeEventListener(
        "webkitfullscreenchange",
        updateFullscreenState
      );
    };
  }, []);

  useEffect(() => {
    function updateViewport() {
      setViewport(getViewportSnapshot());
    }

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add("ft-shop-screen-mode");
    } else {
      document.body.classList.remove("ft-shop-screen-mode");
    }

    return () => {
      document.body.classList.remove("ft-shop-screen-mode");
    };
  }, [isFullscreen]);

  const activeROs = useMemo(() => {
    return shopSourceROs.filter(isVisibleShopRO);
  }, [shopSourceROs]);

  const sortedROs = useMemo(() => {
    const priority = (badge) => {
      if (badge.type === "overdue") return 0;
      if (badge.type === "soon") return 1;
      return 2;
    };

    return [...activeROs].sort((a, b) => {
      const ba = getPromiseBadge(getPromiseTime(a), now);
      const bb = getPromiseBadge(getPromiseTime(b), now);

      const promiseDiff = priority(ba) - priority(bb);
      if (promiseDiff !== 0) return promiseDiff;

      const aMs = safeDate(getPromiseTime(a))?.getTime() || Number.MAX_SAFE_INTEGER;
      const bMs = safeDate(getPromiseTime(b))?.getTime() || Number.MAX_SAFE_INTEGER;

      return aMs - bMs;
    });
  }, [activeROs, now]);

  const toggleFullscreen = useCallback(async () => {
    if (!supportsFullscreen()) return;

    try {
      const currentlyFullscreen = !!(
        document.fullscreenElement || document.webkitFullscreenElement
      );

      if (currentlyFullscreen) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
        return;
      }

      const el = document.documentElement;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      }
    } catch (e) {
      console.warn("Fullscreen request failed:", e?.message || e);
    }
  }, []);

  const overdueCount = useMemo(() => {
    return sortedROs.filter(
      (ro) => getPromiseBadge(getPromiseTime(ro), now).type === "overdue"
    ).length;
  }, [sortedROs, now]);

  const dueSoonCount = useMemo(() => {
    return sortedROs.filter(
      (ro) => getPromiseBadge(getPromiseTime(ro), now).type === "soon"
    ).length;
  }, [sortedROs, now]);

  const cardsPerPage = useMemo(
    () => getShopPageSize(viewport, isFullscreen),
    [viewport, isFullscreen]
  );

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(sortedROs.length / cardsPerPage));
  }, [sortedROs.length, cardsPerPage]);

  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [sortedROs.length, cardsPerPage]);

  useEffect(() => {
    if (pageCount <= 1) return undefined;

    const id = setInterval(() => {
      setPageIndex((current) => (current + 1) % pageCount);
    }, PAGE_ROTATION_MS);

    return () => clearInterval(id);
  }, [pageCount]);

  const currentPageIndex = Math.min(pageIndex, pageCount - 1);
  const pageStart = currentPageIndex * cardsPerPage;
  const visibleROs = sortedROs.slice(pageStart, pageStart + cardsPerPage);

  const pageStyle = useMemo(
    () => ({
      ...shellBase,
      padding: isFullscreen ? "1.1rem 1.35rem 1.25rem" : "1.15rem",
      gap: isFullscreen ? 14 : 14,
      width: "100%",
      boxSizing: "border-box",
    }),
    [isFullscreen]
  );

  const headerStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: isFullscreen
        ? "minmax(320px, 1fr) auto"
        : "minmax(280px, 1fr) auto",
      alignItems: "start",
      gap: 14,
    }),
    [isFullscreen]
  );

  const gridStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: isFullscreen
        ? "repeat(auto-fill, minmax(282px, 1fr))"
        : "repeat(auto-fill, minmax(270px, 1fr))",
      gap: isFullscreen ? 15 : 16,
      alignItems: "stretch",
      alignContent: "start",
    }),
    [isFullscreen]
  );

  return (
    <>
      <style>
        {`
          body.ft-shop-screen-mode,
          body.ft-shop-screen-mode #root {
            background: #0f172a !important;
            min-height: 100vh !important;
          }

          body.ft-shop-screen-mode .sidebar,
          body.ft-shop-screen-mode .app-sidebar,
          body.ft-shop-screen-mode .ft-sidebar,
          body.ft-shop-screen-mode .ft-appFooter {
            display: none !important;
          }

          body.ft-shop-screen-mode .app-shell {
            display: block !important;
            min-height: 100vh !important;
          }

          body.ft-shop-screen-mode .app-main {
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            min-height: 100vh !important;
            overflow-x: hidden !important;
            background: #0f172a !important;
          }

          body.ft-shop-screen-mode .app-main-inner {
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          @media (max-width: 980px) {
            .ft-shop-tv-header {
              grid-template-columns: 1fr !important;
            }

            .ft-shop-tv-controls {
              justify-content: flex-start !important;
            }
          }
        `}
      </style>

      <div style={pageStyle}>
        <header className="ft-shop-tv-header" style={headerStyle}>
          <div style={{ minWidth: 0 }}>
            <h1 style={titleStyle}>Shop TV View</h1>
            <div style={subtitleStyle}>
              Read-only board for active shop work. Overdue and due-soon ROs stay
              highlighted for quick visibility.
            </div>
          </div>

          <div
            className="ft-shop-tv-controls"
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div
              aria-label="Shop screen summary"
              style={{
                display: "flex",
                gap: 9,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <SummaryTile label="Active" value={activeROs.length} />
              <SummaryTile
                label="Overdue"
                value={overdueCount}
                tone={overdueCount ? "danger" : "default"}
              />
              <SummaryTile
                label="Due soon"
                value={dueSoonCount}
                tone={dueSoonCount ? "warn" : "default"}
              />
              {pageCount > 1 ? (
                <SummaryTile
                  label="Page"
                  value={`${currentPageIndex + 1}/${pageCount}`}
                />
              ) : null}
            </div>

            <div
              style={{
                textAlign: "right",
                fontSize: 12,
                color: "#9ca3af",
                minWidth: isFullscreen ? 178 : 158,
              }}
            >
              <div>{formatTimestamp(now)}</div>

              {fullscreenSupported ? (
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  style={{ ...tvButtonStyle, marginTop: 8 }}
                  aria-label={
                    isFullscreen
                      ? "Exit fullscreen shop TV view"
                      : "Enter fullscreen shop TV view"
                  }
                  title={
                    isFullscreen
                      ? "Exit fullscreen shop TV view"
                      : "Enter fullscreen shop TV view"
                  }
                >
                  {isFullscreen ? "×" : "Fullscreen Mode"}
                </button>
              ) : (
                <div style={{ marginTop: 8, color: "#94a3b8" }}>
                  Fullscreen unavailable
                </div>
              )}
            </div>
          </div>
        </header>

        {sortedROs.length === 0 ? (
          <div
            style={{
              fontSize: 15,
              color: "#9ca3af",
              border: "1px dashed rgba(148,163,184,0.35)",
              borderRadius: 16,
              padding: "1.2rem",
              background: "rgba(2,6,23,0.38)",
            }}
          >
            No active repair orders in the shop.
          </div>
        ) : (
          <div
            key={`shop-tv-page-${currentPageIndex}`}
            style={gridStyle}
            aria-label="Active repair orders"
          >
            {visibleROs.map((ro, idx) => {
              const promiseTime = getPromiseTime(ro);
              const badge = getPromiseBadge(promiseTime, now);

              return (
                <ShopROCard
                  key={ro?.id || ro?.roNumber || idx}
                  ro={ro}
                  badge={badge}
                  promiseTime={promiseTime}
                  isFullscreen={isFullscreen}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

/* ===============================
   Small Components
================================ */
function SummaryTile({ label, value, tone = "default" }) {
  return (
    <div style={summaryTileStyle(tone)}>
      <div style={metricLabel}>{label}</div>
      <div
        style={{
          marginTop: 4,
          color: tone === "danger" ? "#fee2e2" : "#f8fafc",
          fontSize: 20,
          lineHeight: 1,
          fontWeight: 950,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ShopROCard({ ro, badge, promiseTime, isFullscreen }) {
  const roLabel = getRoNumber(ro);
  const statusLabel = getDisplayStatus(ro);
  const techName = getAssignedTechName(ro);

  return (
    <article style={shopCardStyle(badge.type, isFullscreen)}>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            marginBottom: 7,
          }}
        >
          <div
            style={{
              fontSize: isFullscreen ? 18 : 17,
              lineHeight: 1.05,
              fontWeight: 900,
              color: "#f8fafc",
              minWidth: 0,
              letterSpacing: "-0.02em",
            }}
          >
            RO# {roLabel}
          </div>
          <span style={statusPillStyle()}>{statusLabel}</span>
        </div>

        <div
          style={{
            fontSize: isFullscreen ? 15 : 14,
            lineHeight: 1.22,
            color: "#f8fafc",
            fontWeight: 700,
          }}
        >
          {getCustomerName(ro)}
        </div>

        <div
          style={{
            marginTop: 3,
            fontSize: isFullscreen ? 14 : 13,
            lineHeight: 1.25,
            color: "#cbd5e1",
          }}
        >
          {getVehicle(ro)}
        </div>

        {techName ? (
          <div
            style={{
              marginTop: 4,
              fontSize: isFullscreen ? 14 : 13,
              color: "#cbd5e1",
            }}
          >
            Tech: <span style={{ color: "#f8fafc", fontWeight: 800 }}>{techName}</span>
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginTop: 11,
        }}
      >
        <div style={{ fontSize: isFullscreen ? 14 : 13, color: "#cbd5e1" }}>
          Promise: {" "}
          <span style={{ color: "#f8fafc", fontWeight: 900 }}>
            {formatTime(promiseTime)}
          </span>
        </div>

        {badge.type !== "none" ? (
          <span style={promisePillStyle(badge.type)}>{badge.label}</span>
        ) : null}
      </div>
    </article>
  );
}
